import { useTranslations } from '@vocab/react';
import {
  Button,
  Box,
  Columns,
  Column,
  Stack,
  Text,
  Inline,
  Dialog,
  TextLink,
  Notice,
  IconDelete,
} from 'braid-design-system';
import { useAtom, useAtomValue } from 'jotai';
import React from 'react';

import translations from '../../../locales.vocab';
import {
  deleteSavedCardDialogOpenAtom,
  isDeleteSaveCardLoadingAtom,
  savedCardInfoAtom,
} from '../../../usecases/CreditCard/CreditCardStore';
import {
  selectedCardBrandAtom,
  surchargeAtom,
} from '../../../usecases/Surcharges/SurchargeStore';
import type { SavedCreditCard } from '../../../usecases/usecaseTypes';
import { BrandIcon } from '../../brands/BrandIcon';

import StripeSecureCardTip from './StripeSecureCardTip';

interface Props {
  savedCreditCard?: SavedCreditCard;
}

const StripeSavedCard = ({ savedCreditCard }: Props) => {
  const { t } = useTranslations(translations);
  const surcharge = useAtomValue(surchargeAtom);
  const selectedCardBrand = useAtomValue(selectedCardBrandAtom);
  const isDeleteSaveCardLoading = useAtomValue(isDeleteSaveCardLoadingAtom);
  const [deleteSaveCardDialog, setDeleteSaveCardDialog] = useAtom(
    deleteSavedCardDialogOpenAtom,
  );
  const savedCardInfo = useAtomValue(savedCardInfoAtom);

  if (!savedCreditCard) {
    return <></>;
  }

  const { onDeleteSavedCard } = savedCreditCard;

  return (
    <Box>
      <Box>
        <Stack space="gutter">
          <Text>{t('stripe.savedcard.desc')}</Text>
          <Columns space="gutter" alignY="center">
            <Column>
              <Inline space="small" alignY="center">
                <BrandIcon
                  brand={selectedCardBrand}
                  active={true}
                  hasSurcharge={false}
                />
                <Inline space="xsmall">
                  <Text>{t('stripe.savedcard.ending')}</Text>
                  <Text weight="strong">{savedCardInfo?.lastDigits}</Text>
                </Inline>
                <Text>|</Text>
                <Inline space="xsmall">
                  <Text>{t('stripe.savedcard.expiry')}</Text>
                  <Text weight="strong" tone="neutral">
                    {savedCardInfo?.expiryMonth}/{savedCardInfo?.expiryYear}
                  </Text>
                </Inline>
                <Text>|</Text>
                <Inline space="xsmall">
                  <Text>
                    <TextLink
                      hitArea="large"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setDeleteSaveCardDialog(true);
                      }}
                    >
                      <IconDelete /> {t('stripe.savedcard.delete')}
                    </TextLink>
                  </Text>
                </Inline>
              </Inline>
            </Column>
          </Columns>
        </Stack>
      </Box>
      {surcharge > 0 && (
        <Notice tone="info">
          <Text>
            {t('a xx% amex surcharge...', {
              surcharge: surcharge.toString(),
            })}
          </Text>
        </Notice>
      )}
      <Box paddingTop="medium">
        <StripeSecureCardTip />
      </Box>

      <Dialog
        id="deleteSavedCardDialog"
        title={t('stripe.savedcard.dialog.title')}
        open={deleteSaveCardDialog}
        onClose={() => setDeleteSaveCardDialog(!deleteSaveCardDialog)}
      >
        <Stack space="medium">
          <Text tone="secondary">{t('stripe.savedcard.dialog.context')}</Text>
          <Box>
            <Inline space="small" align="left">
              <Button
                id="btnDeleteCard"
                size="small"
                loading={isDeleteSaveCardLoading}
                onClick={onDeleteSavedCard}
              >
                {t('stripe.savedcard.dialog.btn.delete')}
              </Button>
              <Button
                variant="transparent"
                size="small"
                onClick={() => {
                  setDeleteSaveCardDialog(!deleteSaveCardDialog);
                }}
              >
                {t('stripe.savedcard.dialog.btn.keep')}
              </Button>
            </Inline>
          </Box>
        </Stack>
      </Dialog>
    </Box>
  );
};

export default StripeSavedCard;
